import { useMemo } from 'react';
import type { ValueOf } from 'type-fest';
import { Decimal } from 'decimal.js';

import type { Product } from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { getBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { useGetFreeTierProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetFreeTierProducts';
import { useOwnedAccountCurrency } from '@hotjar/webapp/hooks/useOwnedAccountCurrency';
import type { CurrencySymbols } from '@hotjar/webapp/domains/common/settings/types';
import type { Site } from '@hotjar/site';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import {
  getProductAndPlanFromSku,
  getV3SkuFromPlanId,
  useProductCatalog,
} from '@hotjar/product-catalog';
import type { legacyPlanIdToV3Sku, Currency } from '@hotjar/product-catalog';

import { useProductCost } from '../../PlanComparisonModal/hooks/useProductCost';
import type { GetCostResult } from '../../PlanComparisonModal/hooks/useProductCost';

interface Props {
  product: Product | null | undefined;
  site: Site | null | undefined;
  billingPeriod?: BillingPeriod;
}

type UseProductPricing = Pick<
  ReturnType<typeof useProductCost>,
  'isLoading' | 'isError' | 'error'
> & {
  symbol: ValueOf<CurrencySymbols>;
  price: GetCostResult | undefined;
};

const DEFAULT_CURRENCY = 'EUR';
const ANNUAL_DISCOUNT_MULTIPLIER = new Decimal('0.8');

/**
 * If you're looking to display the price of a plan in context of a site, you should use this hook over anything else.
 *
 * It abstracts aways the differences between price retrieval for v2 and v3 accounts. It will use the subscription
 * preview method for getting prices for a v2 account, and the v3 product catalog for a v3 account.
 *
 * It uses the derived price vs catalog price terminology, where the derived price is the one derived from the
 * calculations in the subscription preview backend, while the catalog price is the price read from the product catalog.
 *
 * In this hook we assume the following:
 *  * We don't want to display prices for non-self-serve plans.
 *  * The products in `freeAsk` and `freeObserve` are indeed free, and we don't want to query the prices for those.
 *
 * TODO: we should find a way to consolidate `useProductPricing` and `useProductCost`
 *
 * TODO: get rid of the hardcoded annual discount and price calculation
 *
 * TODO: add tests
 */
export const useProductPricing = ({
  product,
  site,
  billingPeriod = 'MONTHLY',
}: Props): UseProductPricing => {
  const isBillingV3 = isBillingV3Enabled(site?.account);
  const v3AllowanceSku = getV3SkuFromPlanId(product?.sku as keyof typeof legacyPlanIdToV3Sku); // TODO remove type assertion
  const v3ProductSku = getProductAndPlanFromSku(v3AllowanceSku).product.id;

  const { feedback_product: freeAsk, analytics_product: freeObserve } = useGetFreeTierProducts();
  const { code: currencyCode, symbol } = useOwnedAccountCurrency();
  const isFreeProduct = product?.sku === freeAsk?.sku || product?.sku === freeObserve?.sku;

  const bundledPlanId = getBundledPlanId({
    analyticsPlanId:
      product?.type === ProductType.Analytics ? product?.sku : freeObserve?.sku || '',
    feedbackPlanId: product?.type === ProductType.Feedback ? product?.sku : freeAsk?.sku || '',
  });

  /**
   * Determines if we want to fetch any price at all. We don't want to run extra queries if we already know that the
   * selected plan is not self-servable or is a free product.
   */
  const shouldFetchPrice = product?.selfServeEnabled && !isFreeProduct;

  // Derives the plan price from a subscription preview result (v2 only)
  const shouldUseDerivedPrice = !isBillingV3 && shouldFetchPrice;

  // Reads the price from the v3 product catalog (v3 only)
  const shouldUseCatalogPrice = isBillingV3 && shouldFetchPrice;

  const productCatalogQuery = useProductCatalog({
    billingPeriod,
    // TODO remove type assertion
    currency: (currencyCode?.toUpperCase() as Currency) ?? DEFAULT_CURRENCY,
    enabled: shouldUseCatalogPrice,
  });

  const catalogPrice = useMemo(
    () =>
      new Decimal(
        shouldUseCatalogPrice
          ? productCatalogQuery.data
              ?.find((v3Product) => v3Product.id === v3ProductSku)
              ?.plans.map((plan) => plan.allowances)
              .flat()
              .find((allowance) => allowance.sku === v3AllowanceSku)?.price ?? 0
          : 0,
      ),
    [productCatalogQuery.data, shouldUseCatalogPrice, v3AllowanceSku, v3ProductSku],
  );

  const derivedPriceQuery = useProductCost({
    site,
    planId: bundledPlanId,
    billingPeriod,
    enabled: shouldUseDerivedPrice,
  });

  // No need to run any queries, return a default object
  if (!shouldFetchPrice) {
    return {
      isLoading: false,
      isError: false,
      error: undefined,
      symbol,
      price: {
        withoutDiscounts: new Decimal(0),
        withDiscounts: new Decimal(0),
      },
    };
  }

  if (shouldUseDerivedPrice) {
    return {
      isLoading: derivedPriceQuery.isLoading,
      isError: derivedPriceQuery.isError,
      error: derivedPriceQuery.error,
      symbol,
      price: derivedPriceQuery.data,
    };
  }

  return {
    isLoading: productCatalogQuery.isLoading,
    isError: productCatalogQuery.isError,
    error: productCatalogQuery.error,
    symbol,
    price: {
      withoutDiscounts: new Decimal(catalogPrice ?? 0),
      withDiscounts: new Decimal(catalogPrice ?? 0).mul(
        billingPeriod === 'ANNUALLY' ? ANNUAL_DISCOUNT_MULTIPLIER : 1,
      ),
    },
  };
};
